import { useLayoutEffect, useRef } from "react";
import { usePresenter } from "./context";
import { pad2 } from "./model";

/**
 * The big section number spans the text beside it: the top of its digits level with the top of the title's
 * capitals, their foot on the baseline of the last line. Font metrics vary with the title's length and the number of points,
 * so it is measured: digit height from the font (canvas), positions from zero-height baseline marks.
 */
function useSpanningNumber(deps: unknown[]) {
  const num = useRef<HTMLSpanElement>(null);
  const numMark = useRef<HTMLSpanElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  const lastMark = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    let live = true;
    const fit = () => {
      const [n, nm, t, lm] = [num.current, numMark.current, title.current, lastMark.current];
      const ctx = document.createElement("canvas").getContext("2d");
      if (!live || !n || !nm || !t || !lm || !ctx) return;
      const style = getComputedStyle(n);
      ctx.font = `${style.fontWeight} 100px ${style.fontFamily}`;
      const m = ctx.measureText(n.textContent ?? "");
      const ink = m.actualBoundingBoxAscent + m.actualBoundingBoxDescent; // per 100px
      // The title's first baseline, then up by its cap height.
      const ts = getComputedStyle(t);
      const size = parseFloat(ts.fontSize);
      ctx.font = `${ts.fontWeight} ${size}px ${ts.fontFamily}`;
      const tm = ctx.measureText("H");
      const halfLeading = (parseFloat(ts.lineHeight) - (tm.fontBoundingBoxAscent + tm.fontBoundingBoxDescent)) / 2;
      const capTop = t.offsetTop + halfLeading + tm.fontBoundingBoxAscent - tm.actualBoundingBoxAscent;
      const height = lm.offsetTop - capTop;
      if (!(ink > 0) || height <= 0) return;
      n.style.fontSize = `${(100 * height) / ink}px`;
      n.style.transform = "";
      // offsetTop is in stage units (unaffected by the stage's scale), so this holds in previews too.
      n.style.transform = `translateY(${lm.offsetTop - nm.offsetTop}px)`;
    };
    fit();
    void document.fonts?.ready.then(fit);
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { num, numMark, title, lastMark };
}

export function SectionSlide({ sectionId }: { sectionId: string }) {
  const entry = usePresenter().model.sections.get(sectionId);
  const { num, numMark, title, lastMark } = useSpanningNumber([
    entry?.section.title,
    entry?.section.ahead.join("\n"),
    entry?.number,
  ]);
  if (!entry) return null;
  const { section, number } = entry;
  const ahead = section.ahead;
  return (
    <div className="slide section-slide">
      <span className="section-num" aria-hidden="true" ref={num}>
        {pad2(number)}
        <span className="baseline-mark" ref={numMark} />
      </span>
      <div className="section-main">
        <span className="yellow-bar" />
        <h1 className="section-h" ref={title}>
          <span className="sr-only">Section {number}: </span>
          {section.title}
          {ahead.length === 0 && <span className="baseline-mark" ref={lastMark} />}
        </h1>
        {ahead.length > 0 && (
          <ul className="ahead">
            {ahead.map((line, i) => (
              <li key={i}>
                <span className="ahead-key">{String.fromCharCode(65 + i)}</span>
                <span>
                  {line}
                  {i === ahead.length - 1 && <span className="baseline-mark" ref={lastMark} />}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
